// Sidebar — the app's left menu (pen Q82 "Search and a minimal left menu",
// decided 3 Nov, on "Left menu – detail design"; collapsible per pen
// I-11..I-14), pure.
//
// Shell chrome filled by registrations (sidebarEntries.ts): it knows no views
// and imports no module. Top to bottom: the school switcher (only for people
// in more than one); the CTA ("New project", neutral: the accent belongs to
// each view's own primary action); the search field that opens the Search
// page, its field focused (owner, 3 Nov); the registered menu entries; the registered recent items, one
// labelled section per registered list (today: projects), in pen's list
// pattern (SidebarRecent.tsx; hidden when empty; 16 below the entries with
// no divider; in the rail, a divider then a letter tile per item, pinned
// first, pen "Shell · Rail", 29 Oct); then the foot: the connection
// indicator (only while something is wrong), registered foot rows and the
// "you" row that opens Settings (SidebarFoot.tsx). Entries that don't work
// yet are not registered as working, so
// they are hidden, never disabled. The fold button sits just outside the
// menu's right edge, level with its header (board 32), floating over the
// page; a page that shows its own (the Studio's top bar) leaves it out.
//
// Three layouts (`SidebarLayout`): expanded; rail (folded to an icon column);
// peek (the expanded menu laid over the content while the rail is hovered,
// I-13). Folding only hides labels: every row keeps its height and every
// icon its place on the x = 26 axis (I-14), so nothing jumps under the
// pointer.
//
// Pure/props-driven: every intent reports up. The one local behaviour is
// arrow-key movement inside the menu entries (view-ephemeral focus).

export type { SidebarLayout };
export { Sidebar };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { KeyboardEvent, ReactNode } from "react";
import { Icon, type IconName } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Kbd, type KbdPlatform } from "../Kbd/Kbd.js";
import { Tooltip } from "../Tooltip/Tooltip.js";
import {
    type RecentListProps,
    recentRowsOf,
    SidebarRecent,
    SidebarRecentTiles,
} from "./SidebarRecent.js";
import { SidebarSchool } from "./SidebarSchool.js";
import {
    countLabel,
    iconOf,
    type SchoolSwitcher,
    type SidebarEntry,
    sidebarSectionsOf,
} from "./sidebarEntries.js";
import "./Sidebar.css";

// One row: icon, then its label while expanded. The tooltip names it when
// the label is hidden (folded) or truncated.
const SidebarItem = ({
    label,
    icon,
    current,
    expanded,
    count = "",
    onSelect,
}: {
    readonly label: string;
    readonly icon: IconName;
    readonly current: boolean;
    readonly expanded: boolean;
    // A count badge ("3", "9+"); "" shows none.
    readonly count?: string;
    readonly onSelect: () => void;
}): ReactNode => (
    <Tooltip content={label} side="right">
        <button
            type="button"
            className="bilda-sidebar__item"
            aria-current={current ? "page" : undefined}
            aria-label={expanded ? undefined : label}
            data-active={current ? "true" : undefined}
            onClick={onSelect}
        >
            <span className="bilda-sidebar__item-icon" aria-hidden="true">
                <Icon name={icon} />
            </span>
            {expanded ? (
                <span className="bilda-sidebar__item-label">{label}</span>
            ) : null}
            {expanded && count !== "" ? (
                <span className="bilda-sidebar__count">{count}</span>
            ) : null}
        </button>
    </Tooltip>
);

// Arrow keys move between the menu entries (one list, spec §5).
const moveFocus = (e: KeyboardEvent<HTMLDivElement>): void => {
    const step = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
    if (step === 0) return;
    const rows = [...e.currentTarget.querySelectorAll("button")];
    const at = rows.findIndex((row) => row.isSameNode(document.activeElement));
    const next = rows[(at + step + rows.length) % rows.length];
    if (at === -1 || next === undefined) return;
    e.preventDefault();
    next.focus();
};

type SidebarLayout = "expanded" | "rail" | "peek";

type SidebarProps = {
    readonly layout: SidebarLayout;
    // Fold (expanded) or unfold (rail, and "Keep menu open" in the peek);
    // null: the page shows its own toggle in that place (the Studio).
    readonly onToggle: (() => void) | null;
    readonly school: SchoolSwitcher;
    // Set up a new project (and open it); null while one is being set up.
    readonly onNewProject: (() => void) | null;
    readonly onOpenSearch: () => void;
    // Which register the ⌘K hint renders in (the host resolves it).
    readonly platform: KbdPlatform;
    // What modules registered: menu entries, recent items, foot rows.
    readonly entries: readonly SidebarEntry[];
    // The recent lists, in order: how each one's items are listed (pins,
    // sort, density) and named.
    readonly recentLists: readonly RecentListProps[];
    // The connection indicator (renders nothing while online).
    readonly connection: ReactNode;
    // The "you" row (SidebarFoot).
    readonly foot: ReactNode;
};

// The fold row's label + glyph per layout (pen I-11..I-13).
const useToggle = (
    layout: SidebarLayout,
): { readonly label: string; readonly icon: IconName } => {
    const { t } = useLingui();
    switch (layout) {
        case "expanded":
            return { label: t`Collapse menu`, icon: "panel-left-close" };
        case "rail":
            return { label: t`Expand menu`, icon: "panel-left-open" };
        case "peek":
            return { label: t`Keep menu open`, icon: "pin" };
        default:
            return exhaustSwitchCase(layout);
    }
};

const Sidebar = ({
    layout,
    onToggle,
    school,
    onNewProject,
    onOpenSearch,
    platform,
    entries,
    recentLists,
    connection,
    foot,
}: SidebarProps): ReactNode => {
    const { t, i18n } = useLingui();
    const labelled = layout !== "rail";
    const toggle = useToggle(layout);
    const sections = sidebarSectionsOf(entries);
    // The rail lists every list's rows as letter tiles (a list still loading
    // shows none there).
    const tiles = recentLists
        .filter((list) => !list.loading)
        .flatMap((list) => recentRowsOf(sections.recent, list, i18n.locale));
    // Only the rail divides its entries from the tiles, and only when there
    // are tiles; expanded, the lists sit 16 below the entries (pen Q82).
    const railDivider = !labelled && tiles.length > 0;
    const newLabel = t`New project`;
    const searchName =
        platform === "mac" ? t`Search, Command K` : t`Search, Control K`;
    return (
        <>
            <nav
                aria-label={t`Main menu`}
                className="bilda-sidebar"
                data-layout={layout}
            >
                <SidebarSchool school={school} expanded={labelled} />

                <Tooltip content={newLabel} side="right">
                    <button
                        type="button"
                        className="bilda-sidebar__cta"
                        aria-label={labelled ? undefined : newLabel}
                        disabled={onNewProject === null}
                        onClick={onNewProject ?? undefined}
                    >
                        <span
                            className="bilda-sidebar__item-icon"
                            aria-hidden="true"
                        >
                            <Icon name="plus" />
                        </span>
                        {labelled ? (
                            <span className="bilda-sidebar__item-label">
                                {newLabel}
                            </span>
                        ) : null}
                    </button>
                </Tooltip>

                <Tooltip content={searchName} side="right">
                    <button
                        type="button"
                        className="bilda-sidebar__search"
                        aria-label={searchName}
                        onClick={onOpenSearch}
                    >
                        <span
                            className="bilda-sidebar__item-icon"
                            aria-hidden="true"
                        >
                            <Icon name="search" />
                        </span>
                        {labelled ? (
                            <>
                                <span className="bilda-sidebar__item-label">
                                    {t`Search`}
                                </span>
                                <Kbd
                                    // One cap ("⌘K"), as the design draws it.
                                    keys={[
                                        platform === "mac" ? "⌘K" : "Ctrl K",
                                    ]}
                                    platform={platform}
                                    className="bilda-sidebar__kbd"
                                    aria-hidden="true"
                                />
                            </>
                        ) : null}
                    </button>
                </Tooltip>

                {/* biome-ignore lint/a11y/noStaticElementInteractions: arrow keys move focus between the buttons inside */}
                <div className="bilda-sidebar__menu" onKeyDown={moveFocus}>
                    {sections.menu.map((entry) => (
                        <SidebarItem
                            key={entry.id}
                            label={entry.label}
                            icon={iconOf(entry.icon)}
                            current={entry.current}
                            expanded={labelled}
                            onSelect={entry.open}
                        />
                    ))}
                </div>

                {railDivider ? (
                    <div
                        className="bilda-sidebar__divider"
                        data-divider="recent"
                        aria-hidden="true"
                    />
                ) : null}
                <div className="bilda-sidebar__recent">
                    {labelled ? (
                        recentLists.map((list) => (
                            <SidebarRecent
                                key={list.id}
                                items={sections.recent.filter(
                                    (item) => item.list === list.id,
                                )}
                                list={list}
                            />
                        ))
                    ) : (
                        <SidebarRecentTiles items={tiles} />
                    )}
                </div>

                <div className="bilda-sidebar__footer">
                    {connection}
                    {sections.foot.map((row) => (
                        <SidebarItem
                            key={row.id}
                            label={row.label}
                            icon={iconOf(row.icon)}
                            current={false}
                            expanded={labelled}
                            count={countLabel(row.count)}
                            onSelect={row.open}
                        />
                    ))}
                    {foot}
                </div>
            </nav>
            {onToggle === null ? null : (
                <Tooltip content={toggle.label} side="right">
                    <IconButton
                        label={toggle.label}
                        variant="ghost"
                        size="sm"
                        className="bilda-sidebar__fold"
                        aria-expanded={layout === "expanded"}
                        onClick={onToggle}
                    >
                        <Icon name={toggle.icon} />
                    </IconButton>
                </Tooltip>
            )}
        </>
    );
};
